import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Filters } from "../../components";
import {
  generalIcons,
  sortOptions,
  validatorCardIcons,
} from "../../utils/constants";
import { useStakingContext } from "../../context/staking-context";
import { filterSearchAndSort, getValidatorImg } from "../../utils/helper";
import ValidatorCard from "../../components/cards/ValidatorCard";
import ValidatorDataTable from "../../components/dataTable/ValidatorDataTable";
import ClipLoaderComponent from "../../components/loader/ClipLoaderComponent";

function AllValidator() {
  const { validators, setSelectedValidator, view, setView } =
    useStakingContext();
  const [selectedTab, setSelectedTab] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSortOption, setSelectedSortOption] = useState("Stake");
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const filteredAndSortedValidators = filterSearchAndSort(validators, {
    statusKey: "",
    selectedStatus: "",
    searchKey: "name",
    searchTerm: searchQuery,
    sortKey: sortOptions[selectedSortOption?.toLowerCase()],
    sortOrder: "desc",
  });

  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const newTransactions = filteredAndSortedValidators?.slice(
      0,
      page * pageSize
    );
    if (JSON.stringify(transactions) !== JSON.stringify(newTransactions)) {
      setTransactions(newTransactions);
    }
  }, [filteredAndSortedValidators, transactions, page, pageSize]);

  useEffect(() => {
    if (view === "grid" && page > 1) {
      fetchTransactions();
    }
  }, [page, view]);

  const fetchTransactions = () => {
    const newTransactions = filteredAndSortedValidators?.slice(
      (page - 1) * pageSize,
      page * pageSize
    );

    if (newTransactions.length < pageSize) {
      setHasMore(false);
    }

    setTransactions((prev) => [...prev, ...newTransactions]);
  };

  const handleScroll = () => {
    if (
      view === "grid" &&
      document.body.scrollHeight - 300 < window.scrollY + window.innerHeight
    ) {
      setIsLoadingMore(true);
    }
  };

  function debounce(func, delay) {
    let timeoutId;
    return function (...args) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  useEffect(() => {
    if (view === "grid") {
      const debouncedHandleScroll = debounce(handleScroll, 500);
      window.addEventListener("scroll", debouncedHandleScroll);

      // Cleanup function
      return () => {
        window.removeEventListener("scroll", debouncedHandleScroll);
      };
    }
  }, [view]);

  useEffect(() => {
    if (isLoadingMore && transactions?.length < validators?.length) {
      setTimeout(() => {
        setPage((prevPage) => prevPage + 1);
        setIsLoadingMore(false);
      }, 1000);
    }
  }, [isLoadingMore]);

  return (
    <div className="relative h-auto pb-16 lg:pb-[100px]">
      <Container>
        <div className="flex items-center">
          <Link
            to={"/"}
            className="flex items-center -tracking-08 w-fit text-xs md:text-xsm font-medium text-dim"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-lightGray/[0.06] p-1 mr-4 *:w-full *:h-full">
              {generalIcons.backArrow}
            </div>
            <span className="opacity-[50%] mr-1">Overview / </span>
          </Link>
          <span className="text-xs md:text-xsm">All Validators</span>
        </div>
        <div className="relative mt-11">
          <h1 className="heading text-textcolor font-bold lg:-tracking-37 flex items-center gap-4 mb-8">
            All Validators
            <span className="p-1 rounded-lg bg-[#1A1A1A] flex items-center justify-center text-[13px] lg:text-sm font-normal leading-4 -tracking-03 text-white w-[26px] self-end md:mb-[7px] lg:w-10 lg:h-10">
              {validators
                ? validators?.filter(
                    (validator) =>
                      validator?.name?.toLowerCase() !== "coming soon"
                  ).length
                : 0}{" "}
            </span>
          </h1>
          <div className="flex items-center gap-3 flex-wrap laptop:flex-nowrap mb-[30px]">
            <Filters
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedSortOption={selectedSortOption}
              setSelectedSortOption={setSelectedSortOption}
              view={view}
              setView={setView}
            />
          </div>

          {view === "grid" ? (
            <>
              {transactions === undefined && (
                <ClipLoaderComponent
                  size={100}
                  customClass={"min-h-[453px] justify-center"}
                />
              )}
              {transactions?.length === 0 && (
                <div className="min-h-[453px] flex items-center justify-center">
                  <h2 className="text-[24px] leading-6 md:text-[30px] md:leading-[30px] font-semibold -tracking-16 text-textcolor">
                    No Validators Found
                  </h2>
                </div>
              )}
              {transactions?.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 laptop:grid-cols-4 gap-3">
                  {transactions?.map((validator) => (
                    <div className="col-span-1" key={validator?.id}>
                      <ValidatorCard
                        validator={validator}
                        icon={getValidatorImg(validator?.name)}
                        title={validator?.name}
                        tagText={validator?.status}
                        arrowIcon={validatorCardIcons.arrow}
                        commission="Commission"
                        commissionValue={validator?.commission === 0 ? "TBD" :validator?.commission * 100 + "%"}
                        checkPoint="Checkpoints signed"
                        checkPointValue={
                          validator?.checkpoint_signed * 100 + "%"
                        }
                        apy="apy"
                        apyValue={validator?.apy === 0 ? "TBD" :validator?.apy * 100 + "%"}
                        stakedIcon={validatorCardIcons.staked}
                        stakedValue={validator?.total_staked.toLocaleString(
                          "en-US"
                        )}
                        setSelectedValidator={setSelectedValidator}
                        performance={validator?.performance}
                        contractAddress={validator?.contract_address}
                      />
                    </div>
                  ))}
                </div>
              )}
              {hasMore && transactions?.length > 12 && (
                <div className="rounded-full border-[1.5px] border-[#1A1A1A] px-4 py-3 flex whitespace-nowrap mt-10 items-center justify-center text-sm/4 font-normal text-primaryWhite/[0.5] max-w-32 mx-auto h-10">
                  {isLoadingMore ? (
                    <ClipLoaderComponent size={20} />
                  ) : (
                    "Load More"
                  )}
                </div>
              )}
            </>
          ) : (
            <ValidatorDataTable
              filteredAndSortedValidators={filteredAndSortedValidators}
              setSelectedValidator={setSelectedValidator}
              isUndefined={filteredAndSortedValidators === undefined}
              hasMore={hasMore}
              setHasMore={setHasMore}
              setIsLoadingMore={setIsLoadingMore}
              view={view}
              isLoadingMore={isLoadingMore}
            />
          )}
        </div>
      </Container>
    </div>
  );
}

export default AllValidator;
